export default () => ({
    overlayVisible: false,
    tooltipVisible: false,
    currentStepIndex: 0,
    tooltipPosition: '',
    highlightStyles: '',
    highlightedElement: null,
    steps: [
        {
            title: 'Main Navigation Bar',
            description: `<h4 class="font-semibold mb-2">Switch Between Audit Modes</h4>
                              <p>This bottom bar lets you toggle between the three main modes:</p>
                              <ul class="list-disc pl-5 mt-2">
                                <li>View Issues of the current page</li>
                                <li>Record Issues to log new violations</li>
                                <li>Browse Site to choose different pages to audit</li>
                              </ul>`,
            target: '#bottom-sidebar',
            position: 'top-center',
            height: 330,
            innerOutlineOffset: true
        },
        {
            title: 'Mode 1: View Accessibility Issues',
            description: `<p>This mode allows you to see all detected accessibility issues on the current page.</p>`,
            target: '#viewIssuesButton',
            position: 'top-left',
            height: 230,
            innerOutlineOffset: false,
            triangleIndicator: true
        },
        {
            title: 'Mode 2: Record New Issues',
            description: `<p>Use this mode to manually record new accessibility violations you discover.</p>`,
            target: '#recordIssuesButton',
            position: 'top-left',
            height: 230,
            innerOutlineOffset: false,
            triangleIndicator: true
        },
        {
            title: 'Mode 3: Navigate the Site',
            description: `<p>Switch to this mode to browse different pages of the site for auditing.</p>`,
            target: '#browseSiteButton',
            position: 'top-left',
            height: 230,
            innerOutlineOffset: false,
            triangleIndicator: true
        },
        {
            title: 'View List of All Issues',
            description: `<p>Click here to see a complete list of all accessibility issues found on this page.</p>`,
            target: '#issuesButton',
            position: 'top-right',
            height: 230,
            innerOutlineOffset: false,
            triangleIndicator: true
        },
        {
            title: 'Scan the Current Page',
            description: `<p>Use this button to run a new accessibility scan on the currently loaded page. This will update the issues detected on this specific page.</p>`,
            target: '#scanPageButton',
            position: 'top-right',
            height: 250,
            innerOutlineOffset: false,
            triangleIndicator: true
        },
        {
            title: 'Manual Audit Tools Bar',
            description: `<p>This toolbar contains various tools to help you during manual accessibility audits.</p>`,
            target: '#tool-bar',
            position: 'bottom-center',
            height: 230,
            innerOutlineOffset: true
        },
        {
            title: 'Filter Issues by Severity',
            description: `<p>Each icon represents a different severity level. Use this filter to view only the issues you care about.</p>`,
            target: '#severity-legend',
            position: 'left-center',
            height: 230,
            offset: 60,
            innerOutlineOffset: false,
            triangleIndicator: true,
            beforeShow: () => {
                if (window.Livewire) {
                    // Set a global flag to disable click.away behavior
                    window.disableSeverityLegendClickAway = true;
                    window.Livewire.dispatch('openSeverityLegend');
                    // Return a promise to delay showing the tooltip until the legend is fully opened
                    return new Promise(resolve => {
                        setTimeout(resolve, 400); // Wait for 300ms for the legend to open
                    });
                }
            },
            afterHide: () => {
                window.disableSeverityLegendClickAway = false;
            }
        }
    ],

    get currentStep() {
        return this.steps[this.currentStepIndex];
    },

    async init() {
        // Check if user has opted out via localStorage (client-side persistence)
        if (localStorage.getItem('aaa_onboarding_complete') === 'true') {
            this.isVisible = false;
            return;
        }

        // Show the first step after a short delay to allow page to fully render
        await this.sleep(1000);

        this.overlayVisible = true;

        await this.sleep(300);

        this.showStep(0);

        // Listen for window resize to reposition tooltip
        window.addEventListener('resize', () => {
            if (this.tooltipVisible) {
                this.positionTooltip();
            }
        });

        // Handle Escape key to close tooltip
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.tooltipVisible) {
                this.complete();
            }
        });
    },

    // Helper function to create a promise that resolves after a delay
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    async showStep(index) {
        if (index >= this.steps.length) {
            this.complete();
            return;
        }

        this.currentStepIndex = index;
        const step = this.currentStep;

        // Clear previous highlight
        this.clearHighlight();

        try {
            // Execute any setup function before showing the tooltip
            if (step.beforeShow) {
                await Promise.resolve(step.beforeShow());
            }

            // Position the tooltip while still invisible
            this.positionTooltip();

            if (!this.tooltipVisible) {
                this.tooltipVisible = true;
            }
            // make sure nextStep button is focused
            const nextStepButton = document.getElementById('nextStep');
            if (nextStepButton) {
                nextStepButton.focus();
            }
        } catch (error) {
            this.nextStep(); // Skip to next step if there's an error
        }
    },

    async positionTooltip() {
        const step = this.currentStep;
        let targetEl;
        targetEl = document.querySelector(step.target);

        if (!targetEl) {
            setTimeout(() => this.nextStep(), 500);
            return;
        }
        targetEl.style.pointerEvents = 'none';
        this.highlightedElement = targetEl;

        // Position the tooltip relative to the target
        const rect = targetEl.getBoundingClientRect();
        const tooltipWidth = step.width || 512; // Approximate width of tooltip
        const tooltipHeight = step.height || 230; // Approximate height of tooltip

        // Calculate positions based on the specified position
        let top, left;
        const offset = step.offset || 30; // Increased space between tooltip and target

        switch (step.position) {
            case 'top-center':
                top = rect.top - tooltipHeight - offset;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
                this.tooltipPosition = 'origin-bottom';
                break;
            case 'bottom-center':
                top = rect.height + offset;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
                this.tooltipPosition = 'origin-top';
                break;
            case 'left-center':
                top = rect.top + rect.height / 2 - tooltipHeight / 2;
                left = rect.width + offset;
                this.tooltipPosition = 'origin-right';
                break;
            case 'top-left':
                top = rect.top - tooltipHeight - offset;
                left = rect.left;
                this.tooltipPosition = 'origin-bottom-left';
                break;
            case 'top-right':
                top = rect.top - tooltipHeight - offset;
                left = rect.right - tooltipWidth;
                this.tooltipPosition = 'origin-bottom-right';
                break;
            default:
                top = rect.bottom + offset;
                left = rect.left;
                this.tooltipPosition = 'origin-top-left';
        }

        // Ensure tooltip stays within viewport
        top = Math.max(20, Math.min(window.innerHeight - tooltipHeight - 20, top));
        left = Math.max(20, Math.min(window.innerWidth - tooltipWidth - 20, left));

        // Set styles for the tooltip
        this.highlightStyles = `
                top: ${top}px;
                left: ${left}px;
            `;


        // Add ring highlight with conditional offset
        targetEl.classList.add('outline-none', 'ring-2', 'ring-blue-500', 'z-50');

        if (step.innerOutlineOffset) {
            // Ring inside the element
            targetEl.classList.add('ring-inset');
        } else {
            // Ring outside the element
            targetEl.classList.add('ring-offset-2');
        }

    },

    clearHighlight() {
        if (this.highlightedElement) {
            this.highlightedElement.style.pointerEvents = '';
            // Remove all ring-related classes
            this.highlightedElement.classList.remove(
                'outline-none',
                'ring-2',
                'ring-blue-500',
                'ring-inset',
                'ring-offset-2',
                'z-50'
            );
            this.highlightedElement = null;
        }
    },

    async nextStep() {
        const currentStep = this.currentStep;

        // Call afterHide handler if it exists
        if (currentStep.afterHide) {
            currentStep.afterHide();
        }

        this.clearHighlight();
        await this.showStep(this.currentStepIndex + 1);
    },

    optOut() {
        this.complete();

        // Notify Livewire component
        if (window.Livewire) {
            window.Livewire.dispatch('optOutOfOnboarding');
        }
    },

    async complete() {
        // Call afterHide handler if it exists for the current step
        const currentStep = this.currentStep;
        if (currentStep && currentStep.afterHide) {
            currentStep.afterHide();
        }

        this.clearHighlight();

        // Hide tooltip first
        this.tooltipVisible = false;

        // Wait for tooltip fade out animation
        await this.sleep(500);

        // Hide overlay
        this.overlayVisible = false;

        // Wait for overlay fade out animation
        await this.sleep(300);

        // Set localStorage and notify Livewire
        localStorage.setItem('aaa_onboarding_complete', 'true');

        if (window.Livewire) {
            window.Livewire.dispatch('completeOnboarding');
        }
    }

});
